<template>
  <div class="merchant-page">
    <div class="header">
      <div>
        <h1>商家端工作台</h1>
        <p class="sub">欢迎回来，{{ merchantName }} · 今日运营概览</p>
      </div>
      <div class="header-actions">
        <el-button type="success" @click="openCreateDialog">上架商品</el-button>
        <el-button type="primary" plain @click="refreshAll" :loading="loading">刷新数据</el-button>
        <el-dropdown @command="handleUserCommand">
          <span class="user-dropdown-link">
            {{ merchantName }}
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">个人信息</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <el-row :gutter="16" class="stat-row">
      <el-col :xs="24" :md="8"><div class="stat-card">今日订单：{{ todayOrderCount }}</div></el-col>
      <el-col :xs="24" :md="8"><div class="stat-card">待发货：{{ pendingOrderCount }}</div></el-col>
      <el-col :xs="24" :md="8"><div class="stat-card">在售商品：{{ totalProductCount }}</div></el-col>
    </el-row>

    <el-row :gutter="16" class="main-row">
      <el-col :xs="24" :lg="14">
        <div class="panel glass">
          <div class="panel-header">
            <h3>商品管理</h3>
            <div class="filters">
              <el-select v-model="categoryFilter" placeholder="全部分类" clearable style="width: 130px;" size="small">
                <el-option v-for="item in categoryOptions" :key="item" :label="item" :value="item" />
              </el-select>
              <el-input v-model="keyword" placeholder="搜索商品名" clearable size="small" style="width: 180px;" />
              <el-button type="success" size="small" @click="openCreateDialog">上架商品</el-button>
            </div>
          </div>

          <el-table :data="filteredProducts" stripe v-loading="loading" height="420" empty-text="暂无商品数据">
            <el-table-column prop="title" label="商品" min-width="180" show-overflow-tooltip />
            <el-table-column prop="category" label="分类" width="100" />
            <el-table-column prop="price" label="价格" width="100">
              <template #default="scope">¥{{ scope.row.price }}</template>
            </el-table-column>
            <el-table-column prop="stock" label="库存" width="100">
              <template #default="scope">
                <el-tag :type="scope.row.stock < 20 ? 'danger' : 'success'" size="small">{{ scope.row.stock }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>

      <el-col :xs="24" :lg="10">
        <div class="panel glass">
          <div class="panel-header">
            <h3>订单看板</h3>
            <el-tag size="small" type="success">近7日趋势（模拟）</el-tag>
          </div>

          <div class="trend-card">
            <div class="trend-metrics">
              <div class="metric-item">
                <span class="metric-label">7日订单</span>
                <strong class="metric-value">{{ mockOrderTotal }}</strong>
              </div>
              <div class="metric-item">
                <span class="metric-label">峰值日期</span>
                <strong class="metric-value">{{ peakTrendDay.day }}（{{ peakTrendDay.orders }}单）</strong>
              </div>
            </div>

            <svg
              class="trend-svg"
              :class="{ ready: chartReady }"
              :viewBox="`0 0 ${trendChart.width} ${trendChart.height}`"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="trendLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stop-color="#7dd3fc" />
                  <stop offset="100%" stop-color="#a78bfa" />
                </linearGradient>
                <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="rgba(125,211,252,0.35)" />
                  <stop offset="100%" stop-color="rgba(167,139,250,0.03)" />
                </linearGradient>
              </defs>

              <g>
                <line
                  v-for="tick in trendChart.yTicks"
                  :key="`grid-${tick.value}`"
                  :x1="trendChart.padding.left"
                  :y1="tick.y"
                  :x2="trendChart.width - trendChart.padding.right"
                  :y2="tick.y"
                  class="trend-grid"
                />
              </g>

              <path :d="trendChart.areaPath" class="trend-area" fill="url(#trendArea)" />
              <path
                :d="trendChart.linePath"
                class="trend-line"
                :style="{
                  strokeDasharray: trendChart.lineLength,
                  strokeDashoffset: chartReady ? 0 : trendChart.lineLength,
                }"
              />

              <g v-for="point in trendChart.points" :key="`p-${point.day}`">
                <circle :cx="point.x" :cy="point.y" r="4.5" class="trend-point" :style="{ transitionDelay: `${point.index * 90}ms` }" />
                <text :x="point.x" :y="point.y - 12" class="trend-value" :style="{ transitionDelay: `${point.index * 90 + 60}ms` }">{{ point.orders }}</text>
                <text :x="point.x" :y="trendChart.height - 10" class="trend-day" :style="{ transitionDelay: `${point.index * 90 + 100}ms` }">{{ point.day }}</text>
              </g>
            </svg>
          </div>
        </div>
      </el-col>
    </el-row>

    <div class="footer-actions">
      <el-button type="success" plain @click="openCreateDialog">上架商品</el-button>
      <el-button type="success" @click="goProducts">商品管理页</el-button>
      <el-button @click="goUserHome">去用户端首页</el-button>
      <el-button type="primary" @click="goOrders">查看用户订单页</el-button>
    </div>

    <!-- 上架商品弹窗 -->
    <el-dialog
      v-model="createDialogVisible"
      title="上架新商品"
      width="460px"
      :modal="false"
      :lock-scroll="false"
    >
      <el-form :model="createForm" label-width="76px">
        <el-form-item label="商品名">
          <el-input v-model="createForm.title" placeholder="请输入商品名称" clearable />
        </el-form-item>
        <el-form-item label="分类">
          <el-select
            v-model="createForm.category"
            placeholder="请选择或输入分类"
            filterable
            allow-create
            default-first-option
            clearable
            style="width: 100%;"
          >
            <el-option v-for="c in categoryOptions" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
        <el-form-item label="价格">
          <el-input-number v-model="createForm.price" :min="0" :precision="2" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="库存">
          <el-input-number v-model="createForm.stock" :min="0" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="图片">
          <el-input v-model="createForm.picUrl" placeholder="可选，图片 URL" clearable />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreateProduct">立即上架</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { productService } from '../../api/product'
import { getCurrentUserId, orderService } from '../../api/order'

const router = useRouter()
const loading = ref(false)
const chartReady = ref(false)
const createDialogVisible = ref(false)
const creating = ref(false)
const createForm = ref({ title: '', category: '', price: 0, stock: 0, picUrl: '' })
const productList = ref([])
const orderList = ref([])
const keyword = ref('')
const categoryFilter = ref('')
const merchantName = ref('演示商家')

const categoryOptions = computed(() => {
  return Array.from(new Set(productList.value.map(item => item.category).filter(Boolean)))
})

const filteredProducts = computed(() => {
  const kw = keyword.value.trim().toLowerCase()
  return productList.value.filter(item => {
    const byCategory = !categoryFilter.value || item.category === categoryFilter.value
    const byKeyword = !kw || (item.title || '').toLowerCase().includes(kw)
    return byCategory && byKeyword
  })
})

const totalProductCount = computed(() => productList.value.length)

const todayOrderCount = computed(() => {
  const today = new Date().toISOString().slice(0, 10)
  return orderList.value.filter(item => String(item.createTime || '').startsWith(today)).length
})

const pendingOrderCount = computed(() => orderList.value.filter(item => Number(item.status) === 0).length)

const trendData = ref([
  { day: '03-14', orders: 8 },
  { day: '03-15', orders: 12 },
  { day: '03-16', orders: 10 },
  { day: '03-17', orders: 15 },
  { day: '03-18', orders: 18 },
  { day: '03-19', orders: 13 },
  { day: '03-20', orders: 21 },
])

const mockOrderTotal = computed(() => trendData.value.reduce((sum, item) => sum + item.orders, 0))

const peakTrendDay = computed(() => {
  return trendData.value.reduce((peak, current) => (current.orders > peak.orders ? current : peak), trendData.value[0])
})

const trendChart = computed(() => {
  const width = 520
  const height = 300
  const padding = { top: 24, right: 20, bottom: 40, left: 30 }
  const plotWidth = width - padding.left - padding.right
  const plotHeight = height - padding.top - padding.bottom
  const maxOrders = Math.max(...trendData.value.map(item => item.orders), 1)
  const axisMax = Math.ceil((maxOrders + 2) / 5) * 5
  const stepX = trendData.value.length > 1 ? plotWidth / (trendData.value.length - 1) : 0

  const points = trendData.value.map((item, index) => {
    const ratio = item.orders / axisMax
    return {
      ...item,
      index,
      x: padding.left + index * stepX,
      y: padding.top + (1 - ratio) * plotHeight,
    }
  })

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ')
  const areaPath = `${linePath} L${points[points.length - 1].x},${height - padding.bottom} L${points[0].x},${height - padding.bottom} Z`

  let lineLength = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    lineLength += Math.sqrt(dx * dx + dy * dy)
  }

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(ratio => {
    const value = Math.round(axisMax * ratio)
    return {
      value,
      y: padding.top + (1 - ratio) * plotHeight,
    }
  })

  return {
    width,
    height,
    padding,
    points,
    linePath,
    areaPath,
    lineLength,
    yTicks,
  }
})

const parseProductData = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.records)) return payload.data.records
  if (Array.isArray(payload?.data?.list)) return payload.data.list
  return []
}

const parseOrderData = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.records)) return payload.records
  return []
}

const loadProducts = async () => {
  const response = await productService.getProductList({ page: 1, size: 200 })
  productList.value = parseProductData(response.data)
}

const loadOrders = async () => {
  const userId = getCurrentUserId() || 1
  const response = await orderService.getOrderList(userId)
  orderList.value = parseOrderData(response.data)
}

const refreshAll = async () => {
  loading.value = true
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    merchantName.value = user.nickname || user.username || '演示商家'
    await Promise.all([loadProducts(), loadOrders()])
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '加载商家数据失败')
  } finally {
    loading.value = false
  }
}

const logout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('portalRole')
  router.push('/login')
}

const goProfile = () => {
  router.push('/profile')
}

const handleUserCommand = (command) => {
  if (command === 'profile') return goProfile()
  if (command === 'logout') return logout()
}

const goUserHome = () => {
  localStorage.setItem('portalRole', 'USER')
  router.push('/home')
}

const goOrders = () => ElMessage.info('订单管理页正在开发中，敬请期待')
const goProducts = () => ElMessage.info('商品管理页正在开发中，敬请期待')

const openCreateDialog = () => {
  createForm.value = { title: '', category: '', price: 0, stock: 0, picUrl: '' }
  createDialogVisible.value = true
}

const submitCreateProduct = async () => {
  if (!createForm.value.title.trim()) {
    ElMessage.warning('请输入商品名')
    return
  }
  creating.value = true
  try {
    await productService.createProduct({
      title: createForm.value.title.trim(),
      category: createForm.value.category?.trim() || '未分类',
      price: Number(createForm.value.price || 0),
      stock: Number(createForm.value.stock || 0),
      picUrl: createForm.value.picUrl?.trim() || null,
      status: 1,
    })
    createDialogVisible.value = false
    ElMessage.success('商品上架成功')
    await loadProducts()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '上架失败')
  } finally {
    creating.value = false
  }
}

onMounted(() => {
  refreshAll()
  requestAnimationFrame(() => {
    chartReady.value = true
  })
})
</script>

<style scoped>
.merchant-page {
  min-height: 100vh;
  padding: 22px;
  color: #fff;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.header h1 {
  margin: 0;
  font-size: 1.7rem;
}

.sub {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.92rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-dropdown-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  cursor: pointer;
}

.stat-row {
  margin-bottom: 16px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
}

.main-row {
  margin-top: 0;
}

.panel {
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 12px 28px rgba(16, 12, 42, 0.18);
}

.glass {
  background: linear-gradient(145deg, rgba(86, 71, 146, 0.26), rgba(48, 32, 98, 0.28));
  border: 1px solid rgba(186, 171, 255, 0.25);
  backdrop-filter: blur(8px);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.filters {
  display: flex;
  gap: 8px;
}

.footer-actions {
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin-top: 16px;
  padding: 12px 0 14px;
  display: flex;
  gap: 10px;
  background: linear-gradient(to top, rgba(18, 12, 50, 0.92) 70%, transparent);
  backdrop-filter: blur(6px);
}

.trend-card {
  min-height: 420px;
  border-radius: 12px;
  padding: 12px 12px 4px;
  background: linear-gradient(180deg, rgba(26, 20, 64, 0.62), rgba(20, 15, 56, 0.36));
  border: 1px solid rgba(167, 139, 250, 0.26);
}

.trend-metrics {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.metric-item {
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.metric-label {
  display: block;
  font-size: 12px;
  color: rgba(233, 230, 255, 0.8);
  margin-bottom: 4px;
}

.metric-value {
  display: block;
  font-size: 16px;
  color: #f4f3ff;
}

.trend-svg {
  width: 100%;
  height: 330px;
}

.trend-grid {
  stroke: rgba(236, 232, 255, 0.18);
  stroke-width: 1;
  stroke-dasharray: 4 6;
}

.trend-line {
  fill: none;
  stroke: url(#trendLine);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 4px 10px rgba(125, 211, 252, 0.28));
  transition: stroke-dashoffset 1.1s ease;
}

.trend-point {
  fill: #c4b5fd;
  stroke: rgba(255, 255, 255, 0.9);
  stroke-width: 1.2;
  opacity: 0;
  transform: translateY(6px);
  transform-origin: center;
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.trend-value {
  fill: #f5f3ff;
  font-size: 11px;
  text-anchor: middle;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.trend-day {
  fill: rgba(230, 225, 255, 0.82);
  font-size: 10px;
  text-anchor: middle;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.trend-area {
  opacity: 0.15;
  transition: opacity 0.8s ease 0.15s;
}

.trend-svg.ready .trend-area {
  opacity: 1;
}

.trend-svg.ready .trend-point,
.trend-svg.ready .trend-value,
.trend-svg.ready .trend-day {
  opacity: 1;
  transform: translateY(0);
}

:deep(.el-table) {
  --el-table-tr-bg-color: rgba(255, 255, 255, 0.06);
  --el-table-header-bg-color: rgba(255, 255, 255, 0.14);
  --el-table-row-hover-bg-color: rgba(125, 211, 252, 0.14);
  --el-table-border-color: rgba(255, 255, 255, 0.1);
  background: transparent;
  border-radius: 10px;
}

:deep(.el-table th.el-table__cell) {
  color: rgba(245, 243, 255, 0.9);
}

:deep(.el-table td.el-table__cell) {
  color: rgba(241, 245, 255, 0.92);
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell) {
  background: rgba(255, 255, 255, 0.12) !important;
  color: rgba(245, 248, 255, 0.96) !important;
}

:deep(.el-table__body tr td.el-table__cell) {
  background: rgba(255, 255, 255, 0.04) !important;
}

:deep(.el-table__inner-wrapper::before) {
  display: none;
}

:deep(.el-table tr),
:deep(.el-table__body-wrapper),
:deep(.el-table__header-wrapper) {
  background: transparent;
}

/* 弹窗深色玻璃主题 */
:deep(.el-dialog) {
  background: linear-gradient(145deg, rgba(30, 22, 72, 0.97), rgba(20, 14, 55, 0.97));
  border: 1px solid rgba(167, 139, 250, 0.32);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(167, 139, 250, 0.12);
  backdrop-filter: blur(20px);
  border-radius: 16px;
}

:deep(.el-dialog__header) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 18px 20px 14px;
}

:deep(.el-dialog__title) {
  color: #e9e6ff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: rgba(220, 215, 255, 0.7);
}

:deep(.el-dialog__headerbtn:hover .el-dialog__close) {
  color: #c4b5fd;
}

:deep(.el-dialog__body) {
  padding: 22px 24px;
}

:deep(.el-dialog .el-form-item__label) {
  color: rgba(220, 215, 255, 0.85);
  font-size: 13px;
}

:deep(.el-dialog .el-input__wrapper) {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(167, 139, 250, 0.3) inset;
}

:deep(.el-dialog .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px rgba(167, 139, 250, 0.55) inset;
}

:deep(.el-dialog .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #a78bfa inset;
}

:deep(.el-dialog .el-input__inner) {
  color: #f0eeff;
}

:deep(.el-dialog .el-input__inner::placeholder) {
  color: rgba(200, 195, 240, 0.45);
}

:deep(.el-dialog .el-select .el-input__wrapper) {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(167, 139, 250, 0.3) inset;
}

:deep(.el-dialog .el-input-number__wrapper) {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(167, 139, 250, 0.3) inset;
}

:deep(.el-dialog .el-input-number .el-input__inner) {
  color: #f0eeff;
}

:deep(.el-dialog__footer) {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 14px 20px 18px;
}
</style>
